import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const ROLE_LABEL = { worker: "CREW", manager: "FOREMAN", admin: "ADMIN" };

// The backend runs every week/deadline calculation in America/Los_Angeles
// (see payperiod.py). The browser must agree, or a device in another timezone
// -- travelling, or simply set wrong -- computes a different "current week"
// than the server enforces, and the crew member is editing or submitting the
// wrong one. Anything that means "today" or "now" for week math goes through
// this, never a bare dayjs().
export const PT_TZ = "America/Los_Angeles";

/** Today's date in Pacific Time as "YYYY-MM-DD".
 *  A plain date string, deliberately: callers pass it straight into dayjs()
 *  for day-granularity comparisons, where a tz-bound object would get
 *  re-interpreted in browser-local time and shift the date back. */
export function todayPT() {
    return dayjs().tz(PT_TZ).format("YYYY-MM-DD");
}

/** Current year in Pacific Time. */
export function currentYearPT() {
    return Number(dayjs().tz(PT_TZ).format("YYYY"));
}

export function mondayOf(d) {
    const m = dayjs(d);
    const dow = m.day(); // 0=Sun..6=Sat
    const diff = dow === 0 ? -6 : 1 - dow;
    return m.add(diff, "day").format("YYYY-MM-DD");
}

export function formatRange(weekStart) {
    const s = dayjs(weekStart);
    const e = s.add(6, "day");
    return `${s.format("MMM D")} – ${e.format("MMM D, YYYY")}`;
}

/** Hours on one entry. Crew type this directly now -- there is no shift to
 *  subtract a lunch from, and the meal period is recorded per day rather than
 *  deducted per entry. Mirrors `entry_hours` on the server; keep them agreeing. */
export function entryHours(e) {
    if (!e) return 0;
    return Math.max(0, parseFloat(e.hours) || 0);
}

/** An entry's hours broken out per phase.
 *
 *  Mirrors `phase_split` on the server, remainder-on-the-last-phase included,
 *  so the figures under the hours box are the ones that will be stored. A
 *  client that rounded differently would show a split adding to 4.99 and save
 *  one adding to 5.00. */
export function phaseSplit(e) {
    const nums = (e?.phase_numbers || []).map(Number);
    if (!nums.length) return {};
    const total = entryHours(e);
    const overrides = e?.phase_hours || {};
    if (Object.keys(overrides).length) {
        return Object.fromEntries(nums.map((n) => [n, round2(overrides[n] ?? overrides[String(n)] ?? 0)]));
    }
    const each = round2(total / nums.length);
    const out = Object.fromEntries(nums.map((n) => [n, each]));
    out[nums[nums.length - 1]] = round2(total - each * (nums.length - 1));
    return out;
}

export function round2(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
}

export function statusLabel(s) {
    return ({
        draft: "Draft",
        submitted: "Submitted",
        approved: "Approved",
        rejected: "Rejected",
        needs_correction: "Needs Correction",
        exported: "Exported",
    }[s] || s);
}

export function statusBadgeColors(s) {
    return ({
        draft: "bg-brand-gold/30 text-brand-charcoal border-brand-gold",
        submitted: "bg-brand-navy/15 text-brand-navy border-brand-navy/40",
        approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
        rejected: "bg-red-100 text-red-800 border-red-300",
        needs_correction: "bg-orange-100 text-orange-800 border-orange-300",
        exported: "bg-slate-200 text-slate-700 border-slate-400",
    }[s] || "bg-brand-stone text-brand-charcoal border-brand-charcoal/30");
}

export function dayCells(weekStart) {
    return Array.from({ length: 7 }).map((_, i) => {
        const d = dayjs(weekStart).add(i, "day");
        return {
            date: d.format("YYYY-MM-DD"),
            dow: d.format("ddd"),
            dayNum: d.format("D"),
            isToday: d.isSame(todayPT(), "day"),
        };
    });
}
